import { QUANT_QUALITY_PENALTY } from './catalog.js';
import { type CloudReference, estimateCloudCost } from './cloud.js';
import type { FitEstimate, ModelProfile, TaskCategory } from './types.js';

export interface WorthRunningLocalScore {
  score: number;
  band: 'yes' | 'maybe' | 'no';
  headline: string;
  reasons: string[];
}

const TASKS_PER_HOUR = 60;
const AVG_IN_TOK = 1500;
const AVG_OUT_TOK = 500;

export function worthRunningLocal(
  model: ModelProfile,
  fit: FitEstimate,
  cloud: CloudReference,
  hoursPerDay = 4,
): WorthRunningLocalScore {
  const localQuality = Math.max(0, model.baseQualityScore - QUANT_QUALITY_PENALTY[fit.quantization]);
  const qualityGap = cloud.baseQualityScore - localQuality;
  const speedRatio = fit.expectedTps / Math.max(1, cloud.approxTps);
  const dailyTasks = TASKS_PER_HOUR * hoursPerDay;
  const cloudDailyCost = estimateCloudCost(cloud, AVG_IN_TOK * dailyTasks, AVG_OUT_TOK * dailyTasks);
  const monthlyCloudCost = cloudDailyCost * 30;

  const reasons: string[] = [];
  let score = 50;

  if (fit.verdict === 'no' || fit.verdict === 'technical') {
    return {
      score: 0,
      band: 'no',
      headline: 'Not worth running local — model barely fits this machine.',
      reasons: ['Fit verdict says the model cannot run usefully here.'],
    };
  }

  if (qualityGap <= 4) {
    score += 20;
    reasons.push(`Quality gap to ${cloud.displayName} is tiny (~${qualityGap.toFixed(1)} points).`);
  } else if (qualityGap <= 10) {
    score += 5;
    reasons.push(
      `Quality gap to ${cloud.displayName} is ~${qualityGap.toFixed(1)} points — usually fine for straightforward edits.`,
    );
  } else {
    score -= 10;
    reasons.push(
      `Quality gap to ${cloud.displayName} is ~${qualityGap.toFixed(1)} points — expect rougher code on harder tasks.`,
    );
  }

  if (speedRatio >= 0.5) {
    score += 15;
    reasons.push(`Speed is ${Math.round(speedRatio * 100)}% of cloud — feels responsive.`);
  } else if (speedRatio >= 0.25) {
    score += 5;
    reasons.push(
      `Speed is ${Math.round(speedRatio * 100)}% of cloud — okay for chat, slow for multi-file work.`,
    );
  } else {
    score -= 10;
    reasons.push(`Speed is ${Math.round(speedRatio * 100)}% of cloud — may bottleneck agent loops.`);
  }

  if (monthlyCloudCost >= 50) {
    score += 20;
    reasons.push(
      `You could save about $${monthlyCloudCost.toFixed(0)}/month vs cloud at ${hoursPerDay}h/day.`,
    );
  } else if (monthlyCloudCost >= 10) {
    score += 8;
    reasons.push(
      `Cloud spend at ${hoursPerDay}h/day is ~$${monthlyCloudCost.toFixed(0)}/month — modest savings from local.`,
    );
  } else {
    score -= 5;
    reasons.push(
      `Cloud spend at this usage is small (~$${monthlyCloudCost.toFixed(0)}/month) — local mostly saves privacy, not money.`,
    );
  }

  score = Math.max(0, Math.min(100, score));
  const band: WorthRunningLocalScore['band'] = score >= 70 ? 'yes' : score >= 45 ? 'maybe' : 'no';
  const headline =
    band === 'yes'
      ? 'Worth running local.'
      : band === 'maybe'
        ? 'Worth trying locally for some tasks.'
        : 'Probably not worth running local yet.';

  return { score, band, headline, reasons };
}

export interface TaskSuitability {
  task: TaskCategory;
  suitable: boolean;
  reason: string;
}

export function suitabilityByTask(model: ModelProfile, fit: FitEstimate): TaskSuitability[] {
  const tasks: TaskCategory[] = ['coding', 'review', 'debugging', 'reasoning', 'docs', 'tool-use'];
  return tasks.map((task) => {
    if (fit.verdict === 'no' || fit.verdict === 'technical') {
      return { task, suitable: false, reason: 'Model does not fit this machine usefully.' };
    }
    if (task === 'tool-use' && fit.expectedTps < 12) {
      return { task, suitable: false, reason: 'Too slow for reliable tool-use chains.' };
    }
    if (
      task === 'reasoning' &&
      model.codingTier !== 'S' &&
      model.codingTier !== 'A' &&
      fit.codingUsefulnessTier !== 'S' &&
      fit.codingUsefulnessTier !== 'A'
    ) {
      return { task, suitable: false, reason: 'Reasoning needs a higher-tier model than this.' };
    }
    if (fit.verdict === 'chat-only' && task !== 'docs') {
      return { task, suitable: false, reason: 'Speed/quality only supports chat and light docs.' };
    }
    return { task, suitable: true, reason: `${fit.codingUsefulnessTier}-tier on your hardware — good fit.` };
  });
}
